import { Dispatch } from "redux";
import axios from "../../utils/axios.tsx";
import { searchFieldActions } from "../../store/search/searchField.tsx";
import { loginAsAnonymousUser } from "../../store/auth.tsx";
import {
  conversationActions,
  fetchConversations,
} from "../../store/conversation.tsx";
import { BASE_URL } from "../../const.ts";

interface ExchangeParams {
  conversation_id?: string;
  exchange_id?: string;
  prompt: string;
  model: string;
}

interface AskQuestionParams {
  dispatch: Dispatch;
  isAuthenticated: boolean;
  stopAnimation: () => void;
  exchangeURL: string;
  exchangeParams: ExchangeParams;
}

export async function streamAsk({
  dispatch,
  isAuthenticated,
  stopAnimation,
  exchangeURL,
  exchangeParams,
}: AskQuestionParams): Promise<void> {
  const streamUrl = `${BASE_URL}/v1/stream_ask/`;

  dispatch(searchFieldActions.setAwaitingAskResponse(true));

  const exchangeResponse = await axios.post(
    exchangeURL,
    JSON.stringify(exchangeParams),
    { withCredentials: true },
  );
  if (!exchangeResponse || !exchangeResponse.data) {
    throw new Error("Failed to initiate streaming exchange.");
  }
  if (
    exchangeResponse.data.prompt === "NEW CONVERSATION" ||
    exchangeResponse.data.prompt.toUpperCase() === "!NC"
  ) {
    dispatch(conversationActions.resetExchanges());
    dispatch(conversationActions.loadAllExchanges([exchangeResponse.data]));
    dispatch(fetchConversations() as any);
    dispatch(
      conversationActions.setSelectedConversation(
        exchangeResponse.data.conversation_id,
      ),
    );
    stopAnimation();
    dispatch(searchFieldActions.setAwaitingAskResponse(false));
    return;
  }

  dispatch(
    conversationActions.appendExchange({
      id: exchangeResponse.data.id,
      prompt: exchangeResponse.data.prompt,
      response: null,
    }),
  );

  try {
    if (!isAuthenticated) {
      dispatch(loginAsAnonymousUser());
    }

    // Step 2: Start the streaming GET
    await new Promise<void>((resolve, reject) => {
      const signedValue = exchangeResponse.data.signed_value;
      let streamUrlWithParams = `${streamUrl}?user_signature=${encodeURIComponent(signedValue)}`;
      if (exchangeParams["conversation_id"]) {
        streamUrlWithParams += `&conversation_id=${exchangeParams["conversation_id"]}`;
      }
      const eventSource = new EventSource(streamUrlWithParams);

      eventSource.onopen = () => {
        console.log("Streaming connection opened.");
      };

      eventSource.onmessage = (event) => {
        let askResponse;
        try {
          askResponse = JSON.parse(event.data);
        } catch (error) {
          console.error("Error parsing SSE message:", error, event.data);
          return;
        }
        if (askResponse.done) {
          console.log("End of stream received.");
          eventSource.close();
          resolve();
        } else {
          try {
            let content;
            if (askResponse.response_type === "Graph") {
              content = JSON.parse(askResponse.content);
            } else {
              content = askResponse.content;
            }
            setTimeout(() => {
              dispatch(
                conversationActions.appendExchange({
                  response: content,
                  type: askResponse.response_type,
                  progress_update: askResponse.progress_update,
                }),
              );
            }, 200); // 200 milliseconds delay to improve stream rendering visually
          } catch (error) {
            console.error("Error appending exchange:", error);
          }
        }
      };

      eventSource.onerror = (error) => {
        console.error("Streaming error:", error);
        eventSource.close();
        reject(error); // Reject the promise in case of an error
      };
    });
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    stopAnimation();
    dispatch(searchFieldActions.setAwaitingAskResponse(false));
    dispatch(fetchConversations() as any);
    dispatch(
      conversationActions.setSelectedConversation(
        exchangeResponse.data.conversation_id,
      ),
    );
  }
}
